import React, { useState, memo } from "react";
import Divider from '@mui/material/Divider';
import { Grid } from "@mui/material";
import { BiMinus, BiPlus } from "react-icons/bi";
import { BottomPanel, DiveTags, LeftAlignedText, StyleZoomLevels, TopPanel, ZoomIconButton } from "../styled/PanelZoomLevel.styled";
import { WebMapContext } from "../../pages/sections/web-map/WebMapGoogleMap";
import { useContext } from "react";
import { useEffect } from "react";
import { DemandScoreTypes, geohashDefaultZoomLvl, sitesDefaultZoomLvl } from "../../constants/WebMap";
import { updateUserPreferedZoom } from "../../services/api/users.api";

const style = {
  width: '100%',
  maxWidth: 360,
  bgcolor: 'background.paper',
};

const PanelZoomLevel = ({
  isCollapsed
}) => {
  const {
    setGeohashZoom,
    setSiteZoom,
    defaultZoomLevels,
    supplyFilters,
    demandFilters,
    eyeIcons,
    geohashZoom,
    mapPlacement
  } = useContext(WebMapContext);

  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user?._id;

  const [supplyDataType, setSupplyDataType] = useState('');
  const [demandDataType, setDemandDataType] = useState('');

  const preferedZoomLevels = user?.preferedZoomLevels ? user.preferedZoomLevels : [];
  const preferedGeohashZoom = preferedZoomLevels.length !== 0 ? preferedZoomLevels.find(level => level.type === "geohash")?.zoomL : geohashDefaultZoomLvl;
  const preferedSiteZoom = preferedZoomLevels.length !== 0 ? preferedZoomLevels.find(level => level.type === "site")?.zoomL : sitesDefaultZoomLvl;

  const [geohashCount, setGeohashCount] = useState(0);
  const [siteCount, setSiteCount] = useState(0);

  useEffect(() => {
    setSiteCount(preferedSiteZoom ? preferedSiteZoom : siteCount)
  }, [])

  useEffect(() => {
    setSupplyDataType(supplyFilters.isBestOperator
      ? `best_${supplyFilters.connectionType.best}_${supplyFilters.dataType.best}`
      : `cust_${supplyFilters.connectionType.custom}_${supplyFilters.dataType.custom}`)
    setDemandDataType(`${demandFilters.demandType}_${demandFilters.connectionType}_${demandFilters.dataType}`)
  }, [demandFilters, supplyFilters]);

  useEffect(() => {
    if (geohashZoom) setGeohashZoom(null); // setting map zoom to google map's max zoom to avoid unwanted api calls while changing data types
    const { supply: eyeS, demand: eyeD, ibs: eyeI } = eyeIcons;
    let zoomVal = preferedGeohashZoom;
    if (eyeS || (eyeD && demandFilters.demandType !== DemandScoreTypes.FINAL) || eyeI) {
      const section = defaultZoomLevels.find(item => item.sectionType === eyeS ? 'supply' : 'demand');
      if (section) {
        const dt = eyeD ? demandFilters.dataType : supplyFilters.dataType[supplyFilters.isBestOperator ? 'best' : 'custom'];
        const dtZoom = section.data.find(dataItem => dataItem.type.toLowerCase() === dt?.toLowerCase());
        if (dtZoom) zoomVal = dtZoom.zoomLevel;
      }

      const ibsSection = eyeI ? defaultZoomLevels.find(item => item.sectionType === 'ibs') : null;
      if(ibsSection){
        const ibsZoom = ibsSection?.data?.find(dataItem => dataItem.type === "OPTR_IBS") || 12
         if (ibsZoom) zoomVal = ibsZoom.zoomLevel;
      }
    }

    setTimeout(() => setGeohashZoom(zoomVal), 400);
    setGeohashCount(zoomVal);
  }, [supplyDataType, demandDataType, eyeIcons.supply, eyeIcons.demand, eyeIcons.capacity, eyeIcons.ibs]);

  const handleIncrement = (type) => {
    if (type === "geohash") {
      geohashCountChange(geohashCount + 1);
    } else if (type === "site") {
      siteCountChange(siteCount + 1);
    }
  };

  const handleDecrement = (type) => {
    if (type === "geohash" && geohashCount > 0) {
      geohashCountChange(geohashCount - 1);
    } else if (type === "site" && siteCount > 0) {
      siteCountChange(siteCount - 1);
    }
  };

  const geohashCountChange = async (count) => {
    if (preferedGeohashZoom !== count && !eyeIcons?.supply && (!eyeIcons?.demand || (eyeIcons?.demand && demandFilters?.demandType === DemandScoreTypes.FINAL))) {
      const newGeoZoomLevel =
        [
          {
            "type": "geohash",
            "zoomL": count
          },
          {
            "type": "site",
            "zoomL": siteCount
          }
        ]
      await updateZoomLevels(userId, newGeoZoomLevel)
    }
    setGeohashZoom(count);
    setGeohashCount(count);
  };

  const siteCountChange = async (count) => {
    if (preferedSiteZoom !== count) {
      const newGeoZoomLevel =
        [
          {
            "type": "geohash",
            "zoomL": geohashCount
          },
          {
            "type": "site",
            "zoomL": count
          }
        ]
      await updateZoomLevels(userId, newGeoZoomLevel)
    }
    setSiteCount(count);
    setSiteZoom(count)
  };

  const updateZoomLevels = async (userId, zoomLevel) => {
    try {
      if (userId) {
        await updateUserPreferedZoom(userId, zoomLevel);
        const user = JSON.parse(localStorage.getItem('user'));
        user.preferedZoomLevels = zoomLevel
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        console.log('User ID is not available');
      }
    } catch (err) {
      console.log(err);
    }
  };

  const geohashZoomLabel = (
    <DiveTags>{`Geohash Resolution:`}</DiveTags>
  );

  const siteZoom = (
    <DiveTags>{`Site Resolution:`}</DiveTags>
  );

  const geohashZoomButtons = (
    <Grid container spacing={2} alignItems="center">
      <Grid item>
        <ZoomIconButton data-testid="geo-zoom-minus" onClick={() => { handleDecrement("geohash"); }}>
          <BiMinus style={{ fontSize: "small", color: "gray" }} /></ZoomIconButton>
      </Grid>
      <Grid item>
        <span style={{ fontSize: "small", color: "gray" }}>{geohashCount}</span>
      </Grid>
      <Grid item>
        <ZoomIconButton data-testid="geo-zoom-plus" onClick={() => { handleIncrement("geohash"); }}>
          <BiPlus style={{ fontSize: "small", color: "gray" }} /></ZoomIconButton>
      </Grid>
    </Grid>
  );

  const siteZoomButtons = (
    <Grid container spacing={2} alignItems="center">
      <Grid item></Grid>
      <Grid item></Grid>
      <Grid item></Grid>
      <Grid item></Grid>
      <Grid item>
        <ZoomIconButton data-testid="site-zoom-minus" onClick={() => { handleDecrement("site"); }}>
          <BiMinus style={{ fontSize: "small", color: "gray" }} /></ZoomIconButton>
      </Grid>
      <Grid item>
        <span style={{ fontSize: "small", color: "gray" }}>{siteCount}</span>
      </Grid>
      <Grid item>
        <ZoomIconButton data-testid="site-zoom-plus"  onClick={() => { handleIncrement("site"); }}>
          <BiPlus style={{ fontSize: "small", color: "gray" }} /></ZoomIconButton>
      </Grid>
    </Grid>
  );

  return (
    <>
      {isCollapsed && mapPlacement !== 'right'
        ? <StyleZoomLevels>
          {/* <TopPanel>
            <Grid container spacing={4} alignItems="center">
              <Grid item>
                <LeftAlignedText>{geohashZoomLabel}</LeftAlignedText>
              </Grid>
              <Grid item>
                <div>{geohashZoomButtons}</div>
              </Grid>
            </Grid>
          </TopPanel>

          <Divider sx={style} /> */}
          <BottomPanel>
            <Grid container spacing={4} alignItems="center" direction="row" justifyContent="flex-start">
              <Grid item xs="auto">
                <LeftAlignedText>{siteZoom}</LeftAlignedText>
              </Grid>
              <Grid item xs="auto">
                <div>{siteZoomButtons}</div>
              </Grid>
            </Grid>
          </BottomPanel>
        </StyleZoomLevels>
        : <></>
      }</>
  )
}

export default memo(PanelZoomLevel)
